import React, { useState } from "react";
import { ChevronRight, Mail, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BackendUrl = process.env.REACT_APP_Backend_Url;

// Couleurs personnalisées
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

function AdminConnection({ chg }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const handleAlert = (message) => {
    toast.success(`${message} !`, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleAlertwar = (message) => {
    toast.warn(`${message} !`, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const connect = async () => {
    if (!email || !password) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${BackendUrl}/api/admin/login`,
        {
          email: email,
          password: password,
        },
        {
          withCredentials: true,
          credentials: "include",
        }
      );

      if (response.status === 200) {
        handleAlert(response.data.message);
        localStorage.setItem(`AdminEcomme`, JSON.stringify(response.data));
        chg(true);
      } else {
        handleAlertwar(response.data.message);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        handleAlertwar(error.response.data.message);
        setError(error.response.data.message);
      } else {
        console.log(error.response || error);
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-lg border border-green-100">
        <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200">
          <CardTitle className="text-2xl font-bold text-center text-green-800">
            Connexion Administrateur - Kassarmou
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-6 px-6">
          {error && (
            <Alert
              variant="destructive"
              className="mb-4 bg-red-50 border-red-200 text-red-800"
            >
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email administrateur
              </label>
              <div className="flex items-center relative">
                <Mail className="absolute left-3 text-gray-500" size={18} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@kassarmou.com"
                  className="pl-10 bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200 transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="Password"
                className="block text-sm font-medium text-gray-700"
              >
                Mot de passe
              </label>
              <div className="flex items-center relative">
                <Lock className="absolute left-3 text-gray-500" size={18} />
                <Input
                  id="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••."
                  className="pl-10 bg-white border border-gray-200 focus:border-green-500 focus:ring focus:ring-green-200 transition-all"
                />
              </div>
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-4 pb-6 px-6">
          <Button
            onClick={connect}
            disabled={isLoading}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 transition-all"
            style={{ backgroundColor: COLORS.green }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Connexion en cours...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                Se connecter <ChevronRight className="ml-2" size={18} />
              </span>
            )}
          </Button>
        </CardFooter>
      </Card>
      <ToastContainer />
    </div>
  );
}

export default AdminConnection;
